import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, CheckCircle, XCircle, Filter, FileText, Settings, Trash2, ExternalLink } from 'lucide-react';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/modals/ConfirmModal';

interface Claim {
  id: string;
  token: string;
  entity_type: string;
  entity_name: string;
  client_name: string | null;
  salesperson: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  response?: ClaimResponse;
}

interface ClaimResponse {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  delivery_date: string;
  within_20_days: string | null;
  claim_type: string;
  claim_type_other: string | null;
  units_affected: string;
  description: string;
  when_detected: string;
  days_after_delivery: number | null;
  photo_urls: string[];
  video_url: string | null;
  washed_used: string;
  wash_detail: string | null;
  affects_commercialization: string;
  expected_solution: string;
  expected_solution_other: string | null;
}

interface ClaimQuestion {
  id: string;
  question_key: string;
  label: string;
  field_type: string;
  options: { value: string; label: string }[];
  is_required: boolean;
  sort_order: number;
}

const CLAIM_TYPE_LABELS: Record<string, string> = {
  defecto_confeccion: 'Defecto de confección',
  error_produccion: 'Error de producción',
  diferencia_estetica: 'Diferencia estética',
  danio_posterior: 'Daño posterior',
  transporte: 'Transporte o logística',
  otro: 'Otro',
};

const UNITS_AFFECTED_LABELS: Record<string, string> = {
  menos_5: 'Menos del 5%',
  mas_5: 'Más del 5%',
  todo: 'Todo el pedido',
};

const WHEN_DETECTED_LABELS: Record<string, string> = {
  al_recibir: 'Al recibir el pedido',
  durante_uso: 'Durante el uso',
  luego_varios: 'Luego de varios usos',
};

const EXPECTED_SOLUTION_LABELS: Record<string, string> = {
  reparacion: 'Reparación',
  reposicion: 'Reposición',
  nota_credito: 'Nota de crédito',
  otra: 'Otra',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  production: 'Producción',
  sample: 'Muestra',
  express_order: 'Pedido Express',
  express_sample: 'Muestra Express',
};

const ClaimsAdmin: React.FC = () => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [questions, setQuestions] = useState<ClaimQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ ids: string[] } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [editQuestion, setEditQuestion] = useState<ClaimQuestion | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [claimsRes, responsesRes, questionsRes] = await Promise.all([
        supabase.from('claims').select('*').order('created_at', { ascending: false }),
        supabase.from('claim_responses').select('*'),
        supabase.from('claim_form_questions').select('*').order('sort_order'),
      ]);
      if (claimsRes.error) throw claimsRes.error;
      if (responsesRes.error) throw responsesRes.error;
      if (questionsRes.error) throw questionsRes.error;

      const responsesMap = new Map<string, ClaimResponse>();
      (responsesRes.data || []).forEach((r: any) => responsesMap.set(r.claim_id, r));

      setClaims(
        (claimsRes.data || []).map((c: any) => ({
          ...c,
          response: responsesMap.get(c.id),
        }))
      );
      setQuestions((questionsRes.data || []).map((q: any) => ({ ...q, options: q.options || [] })));
    } catch (e) {
      console.error('Error loading claims:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = claims.filter((c) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return c.status === 'pending';
    return c.status === 'completed';
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((c) => c.id)));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm?.ids?.length) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('claims').delete().in('id', deleteConfirm.ids);
      if (error) throw error;
      setClaims((prev) => prev.filter((c) => !deleteConfirm.ids.includes(c.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        deleteConfirm.ids.forEach((id) => next.delete(id));
        return next;
      });
      setDeleteConfirm(null);
    } catch (e) {
      console.error('Error deleting claims:', e);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveQuestion = async (q: ClaimQuestion) => {
    try {
      const { error } = await supabase
        .from('claim_form_questions')
        .update({
          label: q.label,
          options: q.options,
          is_required: q.is_required,
          updated_at: new Date().toISOString(),
        })
        .eq('id', q.id);
      if (error) throw error;
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? q : x)));
      setEditQuestion(null);
    } catch (e) {
      console.error('Error saving question:', e);
    }
  };

  const completedCount = claims.filter((c) => c.status === 'completed').length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reclamos</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingQuestions(!editingQuestions)}
            icon={<Settings className="h-4 w-4" />}
          >
            {editingQuestions ? 'Cerrar edición' : 'Editar preguntas'}
          </Button>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
            <FileText className="h-4 w-4" />
            Total reclamos
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{claims.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">Completados</div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{completedCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">Pendientes</div>
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{claims.length - completedCount}</div>
        </div>
      </div>

      {/* Editar preguntas */}
      {editingQuestions && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preguntas del formulario</h2>
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {questions
              .filter((q) => q.field_type !== 'display')
              .map((q) =>
                editQuestion?.id === q.id ? (
                  <div key={q.id} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Etiqueta</label>
                    <input
                      type="text"
                      value={editQuestion.label}
                      onChange={(e) => setEditQuestion({ ...editQuestion, label: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3"
                    />
                    {editQuestion.options?.length > 0 && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Opciones (JSON)</label>
                        <textarea
                          value={JSON.stringify(editQuestion.options, null, 2)}
                          onChange={(e) => {
                            try {
                              const opts = JSON.parse(e.target.value);
                              if (Array.isArray(opts)) setEditQuestion({ ...editQuestion, options: opts });
                            } catch {}
                          }}
                          rows={4}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        />
                      </>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={() => handleSaveQuestion(editQuestion)}>
                        Guardar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditQuestion(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={q.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <span className="text-sm text-gray-900 dark:text-white">{q.label}</span>
                    <Button size="sm" variant="outline" onClick={() => setEditQuestion(q)}>
                      Editar
                    </Button>
                  </div>
                )
              )}
          </div>
        </div>
      )}

      {/* Filtro y acciones */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          >
            <option value="all">Todas</option>
            <option value="pending">Pendientes</option>
            <option value="completed">Completadas</option>
          </select>
        </div>
        {selectedIds.size > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setDeleteConfirm({ ids: Array.from(selectedIds) })}
            icon={<Trash2 className="h-4 w-4" />}
          >
            Eliminar seleccionados ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selectedIds.size === filtered.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Entidad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Estado
                  </th>
                  <th className="px-4 py-3 w-20" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {c.entity_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {ENTITY_TYPE_LABELS[c.entity_type] || c.entity_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {c.client_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {format(new Date(c.created_at), 'dd/MM/yyyy', { locale: es })}
                      </td>
                      <td className="px-4 py-3">
                        {c.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                            <CheckCircle className="h-3 w-3" />
                            Completado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                            <XCircle className="h-3 w-3" />
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {c.status === 'pending' && (
                          <a
                            href={`/reclamo/${c.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg inline-flex"
                            title="Abrir formulario"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm({ ids: [c.id] })}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{expandedId === c.id ? '▲' : '▼'}</td>
                    </tr>
                    {expandedId === c.id && c.response && (
                      <tr>
                        <td colSpan={9} className="px-4 py-4 bg-gray-50 dark:bg-gray-900/50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Contacto:</strong>{' '}
                              {c.response.contact_name}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Email:</strong>{' '}
                              {c.response.contact_email}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Teléfono:</strong>{' '}
                              {c.response.contact_phone}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Fecha entrega:</strong>{' '}
                              {c.response.delivery_date}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Tipo:</strong>{' '}
                              {CLAIM_TYPE_LABELS[c.response.claim_type] || c.response.claim_type}
                              {c.response.claim_type_other && ` (${c.response.claim_type_other})`}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Unidades afectadas:</strong>{' '}
                              {UNITS_AFFECTED_LABELS[c.response.units_affected] || c.response.units_affected}
                            </div>
                            <div className="sm:col-span-2 lg:col-span-3">
                              <strong className="text-gray-700 dark:text-gray-300">Descripción:</strong>{' '}
                              {c.response.description}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Detectado:</strong>{' '}
                              {WHEN_DETECTED_LABELS[c.response.when_detected] || c.response.when_detected}
                            </div>
                            <div>
                              <strong className="text-gray-700 dark:text-gray-300">Solución esperada:</strong>{' '}
                              {EXPECTED_SOLUTION_LABELS[c.response.expected_solution] || c.response.expected_solution}
                              {c.response.expected_solution_other && ` (${c.response.expected_solution_other})`}
                            </div>
                            {c.response.photo_urls?.length > 0 && (
                              <div className="sm:col-span-2 lg:col-span-3">
                                <strong className="text-gray-700 dark:text-gray-300 block mb-2">Fotos:</strong>
                                <div className="flex flex-wrap gap-2">
                                  {c.response.photo_urls.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                                      <img src={url} alt="" className="h-24 w-24 object-cover rounded-lg border" />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            {c.response.video_url && (
                              <div className="sm:col-span-2 lg:col-span-3">
                                <strong className="text-gray-700 dark:text-gray-300">Video:</strong>{' '}
                                <a href={c.response.video_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                  {c.response.video_url}
                                </a>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => !deleting && setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title={deleteConfirm?.ids?.length === 1 ? 'Eliminar reclamo' : 'Eliminar reclamos'}
        message={
          deleteConfirm?.ids?.length === 1
            ? '¿Estás seguro de que deseas eliminar este reclamo? Esta acción no se puede deshacer.'
            : `¿Estás seguro de que deseas eliminar ${deleteConfirm?.ids?.length || 0} reclamos? Esta acción no se puede deshacer.`
        }
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};

export default ClaimsAdmin;
